import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Edit,
  Trash2,
  Plus,
  FileText,
  Power,
  PowerOff,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Users,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Workflow, WorkflowStage, StageData, WorkflowContext, ApproverUser } from './types';
import { StageDrawer } from './StageDrawer';
import { WorkflowViewToggle } from './WorkflowViewToggle';
import type { WorkflowView } from './WorkflowViewToggle';
import { WorkflowListView } from './WorkflowListView';
import { WorkflowVisualFlow } from './WorkflowVisualFlow';
import type { ApprovalStage } from '@/types/approvalWorkflow';
import { findPlaceholderStages } from './placeholderStageUtils';
import { PlaceholderStageWarning } from './PlaceholderStageWarning';

interface WorkflowDetailsPageProps {
  workflow: Workflow;
  onBack: () => void;
  onUpdateStage: (stageId: string, stageData: StageData) => void;
  onSaveDraft?: (stageId: string, stageData: StageData) => Promise<void>; // Save draft to backend without closing wizard
  onDeleteStage?: (stageId: string) => void;
  onAddStage?: (stageName?: string, placeholderStageId?: string) => void; // UPDATED: Accept optional stage name and placeholder ID
  initialEditingStageId?: string | null;
  onClearInitialEditingStageId?: () => void;
  initialStageName?: string; // NEW: Pre-fill stage name when creating from placeholder
  onUpdateWorkflowStatus?: (workflowId: string, newStatus: Workflow['status']) => void; // NEW: Status update callback
  approverUsers?: ApproverUser[]; // Live users for the Specific Person dropdown
  onReorderStages?: (stages: WorkflowStage[]) => void; // NEW: Reorder stages callback
  onDuplicateStage?: (stageId: string) => void; // NEW: Duplicate stage callback
  onPreviousWorkflow?: () => void; // NEW: Navigate to previous workflow
  onNextWorkflow?: () => void; // NEW: Navigate to next workflow
  hasPreviousWorkflow?: boolean; // NEW: Is there a previous workflow?
  hasNextWorkflow?: boolean; // NEW: Is there a next workflow?
  currentWorkflowIndex?: number; // NEW: Current workflow index (0-based)
  totalWorkflows?: number; // NEW: Total number of workflows
  onDeleteWorkflow?: (workflowId: string) => void;
  currentView?: WorkflowView;
  onViewChange?: (view: WorkflowView) => void;
}

export function WorkflowDetailsPage({
  workflow,
  onBack,
  onUpdateStage,
  onSaveDraft,
  onDeleteStage,
  onAddStage,
  initialEditingStageId,
  onClearInitialEditingStageId,
  initialStageName,
  onUpdateWorkflowStatus,
  approverUsers,
  onReorderStages,
  onDuplicateStage,
  onPreviousWorkflow,
  onNextWorkflow,
  hasPreviousWorkflow,
  hasNextWorkflow,
  currentWorkflowIndex,
  totalWorkflows,
  onDeleteWorkflow,
  currentView: controlledView,
  onViewChange,
}: WorkflowDetailsPageProps) {
  const [editingStageId, setEditingStageId] = useState<string | null>(initialEditingStageId || null);
  const [drawerOpen, setDrawerOpen] = useState(!!initialEditingStageId);
  const [internalView, setInternalView] = useState<WorkflowView>('list');
  const currentView = controlledView ?? internalView;
  const setCurrentView = onViewChange ?? setInternalView;
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<Workflow['status'] | null>(null);

  // Detect placeholder stages
  const placeholders = findPlaceholderStages(workflow);
  const hasPlaceholders = placeholders.length > 0;

  // Watch for initialEditingStageId changes (when "Add Stage" or "Save & Add Another" is clicked)
  React.useEffect(() => {
    if (initialEditingStageId) {
      setEditingStageId(initialEditingStageId);
      setDrawerOpen(true);
    }
  }, [initialEditingStageId]);

  const editingStage = workflow.stages.find((s) => s.id === editingStageId);

  const handleEditStage = (stageId: string) => {
    setEditingStageId(stageId);
    setDrawerOpen(true);
  };

  const handleSaveStage = (stageData: StageData) => {
    if (editingStageId) {
      onUpdateStage(editingStageId, stageData);
    }
    setDrawerOpen(false);
    setEditingStageId(null);
    if (onClearInitialEditingStageId) {
      onClearInitialEditingStageId();
    }
  };

  const handleSaveAndAddAnother = () => {
    // Save the current stage first (data is already saved via handleSaveStage)
    // Then trigger parent to create a new stage
    if (onAddStage) {
      onAddStage();
    }
    
    // The drawer will stay open and switch to the new stage via initialEditingStageId
    // No need to close or reset here - the parent will handle it
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingStageId(null);
    if (onClearInitialEditingStageId) {
      onClearInitialEditingStageId();
    }
  };

  const handleStatusChangeRequest = (newStatus: Workflow['status']) => {
    setPendingStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleStatusChangeConfirm = () => {
    if (pendingStatus && onUpdateWorkflowStatus) {
      onUpdateWorkflowStatus(workflow.id, pendingStatus);
    }
    setStatusDialogOpen(false);
    setPendingStatus(null);
  };

  const getStatusDialogContent = () => {
    if (!pendingStatus) return { title: '', description: '' };

    switch (pendingStatus) {
      case 'active':
        return {
          title: 'Activate Workflow',
          description: `Are you sure you want to activate "${workflow.name}"? This workflow will become available for use in approval processes.`,
        };
      case 'inactive':
        return {
          title: 'Deactivate Workflow',
          description: `Are you sure you want to deactivate "${workflow.name}"? Any in-progress approvals using this workflow will continue, but new approvals will not use this workflow.`,
        };
      case 'draft':
        return {
          title: 'Move to Draft',
          description: `Are you sure you want to move "${workflow.name}" back to draft status? This workflow will not be available for use until activated again.`,
        };
      default:
        return { title: '', description: '' };
    }
  };

  const getStatusBadge = (status: WorkflowStage['status']) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Active
          </Badge>
        );
      case 'configured':
        return (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Configured
          </Badge>
        );
      case 'draft':
        return (
          <Badge variant="outline" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Draft
          </Badge>
        );
    }
  };

  const getWorkflowStatusBadge = (status: Workflow['status']) => {
    let badgeContent;
    let badgeClass = "";
    let icon;

    switch (status) {
      case 'draft':
        badgeContent = 'Draft';
        badgeClass = "border-gray-300 text-gray-700 bg-gray-50";
        icon = <AlertCircle className="h-3 w-3 mr-1" />;
        break;
      case 'active':
        badgeContent = 'Active';
        badgeClass = "bg-green-500 text-white border-green-500";
        icon = <CheckCircle2 className="h-3 w-3 mr-1" />;
        break;
      case 'inactive':
        badgeContent = 'Inactive';
        badgeClass = "bg-gray-500 text-white border-gray-500";
        icon = <PowerOff className="h-3 w-3 mr-1" />;
        break;
      default:
        return null;
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="group cursor-pointer hover:opacity-80 transition-all flex items-center gap-1">
            <Badge className={`gap-1 ${badgeClass} group-hover:ring-2 group-hover:ring-primary/20 transition-all`}>
              {icon}
              {badgeContent}
              <ChevronDown className="h-3 w-3 ml-1 group-hover:translate-y-0.5 transition-transform" />
            </Badge>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {status !== 'draft' && (
            <DropdownMenuItem onClick={() => handleStatusChangeRequest('draft')}>
              <AlertCircle className="h-4 w-4 mr-2" />
              Move to Draft
            </DropdownMenuItem>
          )}
          {status !== 'active' && (
            <DropdownMenuItem onClick={() => handleStatusChangeRequest('active')}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Activate
            </DropdownMenuItem>
          )}
          {status !== 'inactive' && (
            <DropdownMenuItem onClick={() => handleStatusChangeRequest('inactive')}>
              <PowerOff className="h-4 w-4 mr-2" />
              Deactivate
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Create workflow context for the wizard
  const createWorkflowContext = (stage: WorkflowStage): WorkflowContext => {
    return {
      workflowId: workflow.id,
      workflowName: workflow.name,
      currentStageNumber: stage.order,
      currentStageId: stage.id,
      currentStageName: stage.name,
      totalStages: workflow.stages.length,
      allStages: workflow.stages.map((s) => ({
        id: s.id,
        name: s.name,
        order: s.order,
      })),
    };
  };

  // Convert WorkflowStage to ApprovalStage format for the new components
  const convertToApprovalStages = (): ApprovalStage[] => {
    return workflow.stages.map((stage) => {
      // Extract rules from stageData.smartRules
      const smartRules = stage.stageData?.smartRules;
      const hasRules = smartRules && (
        (smartRules.rules && smartRules.rules.length > 0) ||
        (smartRules.onApproved && smartRules.onApproved.type !== 'continue') ||
        (smartRules.onRejected && smartRules.onRejected.type !== 'reject') ||
        smartRules.timeout?.enabled
      );
      
      // Extract notifications from stageData.notifications
      const notifications = stage.stageData?.notifications;
      const hasNotifications = notifications && (
        (notifications.onApproved && notifications.onApproved.length > 0) ||
        (notifications.onRejected && notifications.onRejected.length > 0)
      );
      
      return {
        id: stage.id,
        stageNumber: stage.order,
        name: stage.name,
        description: stage.description,
        approvers: stage.stageData?.approver ? [{
          id: stage.stageData.approver.specificUserId || '',
          name: stage.stageData.approver.specificUserName || '',
          email: '',
          type: stage.stageData.approver.approverType === 'role' ? 'role' : 'user',
          role: stage.stageData.approver.roleType,
        }] : [],
        // Rules are tracked via routingConfigured flag - ApprovalStage.rules uses a different format
        // than the wizard's smart rules, so we leave it empty and use routingConfigured for display
        rules: [],
        notificationSettings: {
          enabled: hasNotifications || false,
        },
        // Include routing info for display purposes
        routingConfigured: hasRules || false,
      };
    });
  };

  const handleEditStageFromList = (stage: ApprovalStage) => {
    const workflowStage = workflow.stages.find((s) => s.id === stage.id);
    if (workflowStage) {
      handleEditStage(workflowStage.id);
    }
  };

  const handleDuplicateStage = (stage: ApprovalStage) => {
    const workflowStage = workflow.stages.find((s) => s.id === stage.id);
    if (workflowStage && onDuplicateStage) {
      onDuplicateStage(workflowStage.id);
    } else {
    }
  };

  const handleMoveStageUp = (stage: ApprovalStage) => {
    const currentIndex = workflow.stages.findIndex((s) => s.id === stage.id);
    if (currentIndex > 0 && onReorderStages) {
      const newStages = [...workflow.stages];
      [newStages[currentIndex - 1], newStages[currentIndex]] = 
        [newStages[currentIndex], newStages[currentIndex - 1]];
      
      // Update order numbers
      newStages.forEach((s, i) => {
        s.order = i + 1;
      });
      
      onReorderStages(newStages);
    }
  };

  const handleMoveStageDown = (stage: ApprovalStage) => {
    const currentIndex = workflow.stages.findIndex((s) => s.id === stage.id);
    if (currentIndex < workflow.stages.length - 1 && onReorderStages) {
      const newStages = [...workflow.stages];
      [newStages[currentIndex], newStages[currentIndex + 1]] = 
        [newStages[currentIndex + 1], newStages[currentIndex]];
      
      // Update order numbers
      newStages.forEach((s, i) => {
        s.order = i + 1;
      });
      
      onReorderStages(newStages);
    }
  };

  const handleDeleteStage = (stage: ApprovalStage) => {
    if (onDeleteStage) {
      onDeleteStage(stage.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{workflow.name}</h1>
              {getWorkflowStatusBadge(workflow.status)}
            </div>
            <p className="text-sm text-muted-foreground">
              {workflow.stages.length} stage{workflow.stages.length !== 1 ? 's' : ''} • {' '}
              {workflow.assignedToCount !== undefined ? `${workflow.assignedToCount} employee${workflow.assignedToCount !== 1 ? 's' : ''}` : '0 employees'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Workflow Navigation */}
          {totalWorkflows !== undefined && totalWorkflows > 1 && (
            <>
              <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted/50">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onPreviousWorkflow}
                  disabled={!hasPreviousWorkflow}
                  className="h-7 w-7 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground px-2">
                  {(currentWorkflowIndex ?? 0) + 1} of {totalWorkflows}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onNextWorkflow}
                  disabled={!hasNextWorkflow}
                  className="h-7 w-7 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Separator orientation="vertical" className="h-6" />
            </>
          )}
          
          {onAddStage && (
            <Button onClick={() => onAddStage()} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Stage
            </Button>
          )}
        </div>
      </div>

      <Separator />

      {/* Placeholder Stage Warning */}
      {hasPlaceholders && (
        <PlaceholderStageWarning 
          placeholders={placeholders}
          onCreateStage={onAddStage ? (stageName, placeholderStageId) => onAddStage(stageName, placeholderStageId) : undefined}
        />
      )}

      {/* View Toggle */}
      {workflow.stages.length > 0 && (
        <WorkflowViewToggle currentView={currentView} onViewChange={setCurrentView} />
      )}

      {/* Workflow Views */}
      {workflow.stages.length > 0 ? (
        <>
          {currentView === 'list' ? (
            <WorkflowListView 
              stages={convertToApprovalStages()} 
              onEditStage={handleEditStageFromList}
              onDuplicateStage={handleDuplicateStage}
              onMoveStageUp={handleMoveStageUp}
              onMoveStageDown={handleMoveStageDown}
              onDeleteStage={handleDeleteStage}
              showPlaceholderInfo={hasPlaceholders}
            />
          ) : (
            <WorkflowVisualFlow 
              stages={convertToApprovalStages()} 
              onEditStage={(stageId) => handleEditStage(stageId)}
            />
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4 max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Build Your Approval Workflow</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add stages to define your approval process. Each stage represents a step where 
                  someone reviews and approves or rejects the submission.
                </p>
              </div>
              <div className="space-y-2">
                {onAddStage && (
                  <Button onClick={() => onAddStage()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Stage
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  💡 Tip: Start with a simple single-stage approval and add more complexity later
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stage Drawer */}
      <StageDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDrawer();
          }
        }}
        existingStage={editingStage?.stageData}
        onSave={handleSaveStage}
        onSaveAndAddAnother={handleSaveAndAddAnother}
        initialStageName={initialStageName}
        approverUsers={approverUsers}
        workflowId={workflow.id}
        workflowName={workflow.name}
        availableStages={workflow.stages
          .filter(s => s.id !== editingStageId)
          .map(s => ({ id: s.id, name: s.name, order: s.order }))}
      />

      {/* Status Change Confirmation Dialog */}
      <AlertDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getStatusDialogContent().title}</AlertDialogTitle>
            <AlertDialogDescription>
              {getStatusDialogContent().description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingStatus(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleStatusChangeConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
